import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChefHat, Search, Camera, CheckCircle2, AlertTriangle, Sprout, Trash2, Clock, PackageCheck,
  Play, Plus, Warehouse, Lock, Settings, Layers, ListChecks, ArrowRight, ArrowLeft, Box,
  Boxes, Flame, Scale, Undo2, History, ChevronLeft, BarChart3, Soup, X, Save, Target, Zap,
  Sunrise, ShieldCheck, Moon,
} from "lucide-react";
import api, { fetchBlobUrl } from "../lib/api";
import { SOPSection } from "../components/SOPChecklist";
import { Card, Badge, Button, PageHeader, PageLoader, Modal } from "../lib/ui";
import { ProductAvatar } from "../components/visual";
import { PhotoEvidence } from "../components/PhotoEvidence";
import { fmtNum } from "../lib/constants";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const r1 = (n) => Math.round(n * 10) / 10;
const REASONS = ["bone", "fat", "skin", "shell", "stem", "trimming", "damaged_leaves", "cleaning", "other"];

function PrepPhoto({ path, alt, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true, obj = null;
    if (path) fetchBlobUrl(`/prepare/photo/${path}`).then((u) => { if (live) { obj = u; setUrl(u); } }).catch(() => {});
    return () => { live = false; if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  if (!path) return null;
  return url ? <img src={url} alt={alt} className={className} /> : <div className={`${className} animate-pulse bg-stone-100`} />;
}

function PhotoUpload({ which, value, uploading, onPick, t }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold text-ink-soft">{which === "before" ? t("Before Photo") : t("After Photo")}</p>
      <label className="flex h-24 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-line hover:border-primary" data-testid={`mp-${which}-upload`}>
        {value?.preview ? <img src={value.preview} alt={which} className="h-full w-full object-cover" />
          : uploading ? <span className="text-xs text-ink-mute">{t("Uploading…")}</span>
          : <span className="flex flex-col items-center text-xs text-ink-mute"><Camera size={18} /> {t("Add photo")}</span>}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0], which)} />
      </label>
    </div>
  );
}

function Kpi({ icon: Icon, value, label, tone, testid }) {
  const tones = { emerald: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", violet: "bg-violet-50 text-violet-600", orange: "bg-orange-50 text-orange-600", blue: "bg-blue-50 text-blue-600" };
  return (
    <Card className="flex items-center gap-3 p-4" data-testid={testid}>
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}><Icon size={20} /></span>
      <div className="min-w-0">
        <p className="font-heading text-2xl font-extrabold leading-none text-ink" data-testid={`${testid}-value`}>{value}</p>
        <p className="mt-1 text-xs font-bold text-ink-soft">{label}</p>
      </div>
    </Card>
  );
}

export default function MejaPrepare() {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("create_prepare");
  const canConfig = hasPermission("manage_prep_config");

  const [data, setData] = useState(null);
  const [dash, setDash] = useState(null);
  const [queue, setQueue] = useState(null);
  const [view, setView] = useState("board"); // board | history
  const [hist, setHist] = useState(null);
  const [busy, setBusy] = useState(false);

  const [whList, setWhList] = useState([]);
  const [canSwitch, setCanSwitch] = useState(false);
  const [whId, setWhId] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [wizardPreset, setWizardPreset] = useState(null); // { source_product_id, suggested_qty, output_product_id }
  const [finItem, setFinItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  const toast = useToast();

  const loadWarehouses = useCallback(async () => {
    try {
      const { data } = await api.get("/prepare/warehouses");
      setWhList(data.warehouses || []); setCanSwitch(!!data.can_switch);
      setWhId((cur) => cur || data.active?.id || "central");
    } catch { setWhId((cur) => cur || "central"); }
  }, []);

  const loadSession = useCallback(async () => {
    const { data } = await api.get("/prepare/session/active", { params: whId ? { warehouse: whId } : {} });
    setData(data);
  }, [whId]);
  const loadDash = useCallback(async () => {
    try { const { data } = await api.get("/prepare/session/dashboard", { params: whId ? { warehouse: whId } : {} }); setDash(data); } catch { setDash(null); }
  }, [whId]);
  const loadQueue = useCallback(async () => {
    try { const { data } = await api.get("/prepare/queue", { params: whId ? { warehouse: whId } : {} }); setQueue(data); }
    catch { setQueue(null); }
  }, [whId]);
  // Phase 2B — Kitchen SOP checklists for today (accordion sections).
  const [sop, setSop] = useState(null);
  const loadSop = useCallback(async () => {
    try { const { data } = await api.get("/prepare/sop/today", { params: whId ? { warehouse: whId } : {} }); setSop(data); }
    catch { setSop(null); }
  }, [whId]);

  const refresh = useCallback(() => { loadSession(); loadDash(); loadQueue(); loadSop(); }, [loadSession, loadDash, loadQueue, loadSop]);

  useEffect(() => { loadWarehouses(); }, [loadWarehouses]);
  useEffect(() => { if (whId) refresh(); }, [whId, refresh]);
  useEffect(() => { if (view === "history" && whId) api.get("/prepare/session/history", { params: { warehouse: whId } }).then(({ data }) => setHist(data.sessions || [])).catch(() => setHist([])); }, [view, whId]);

  // Router-state preset: opening Meja Prepare from Ready-to-Cook Inventory ("Prepare" CTA).
  // Reuses the same wizard + queue; NEVER duplicates preparation logic.
  useEffect(() => {
    const st = location.state;
    if (!st?.preset) return;
    if (st.warehouse_id) setWhId(st.warehouse_id);
    setWizardPreset(st.preset);
    // Clear router state so refreshing the page doesn't re-open the wizard.
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  const openSession = async () => {
    setBusy(true);
    try { await api.post("/prepare/session/open", { warehouse: whId || undefined }); await refresh(); toast.success(t("Preparation session opened")); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); } finally { setBusy(false); }
  };
  const [closeModal, setCloseModal] = useState(null); // { unmet:[], reasons:[{key,label}] }
  const showCloseError = (e) => {
    const d = e.response?.data?.detail;
    if (d && typeof d === "object" && d.sop_blockers) {
      toast.error(`${d.message}: ${d.sop_blockers.map((b) => b.label).join(", ")}`);
    } else {
      toast.error((d && typeof d === "object" ? d.message : d) || t("Failed"));
    }
  };
  const closeSession = async () => {
    // Phase 2B — SOP items with block="block" must pass before closing (server enforced too).
    const blockers = sop?.close_blockers || [];
    if (blockers.length > 0) {
      toast.error(`SOP wajib belum selesai: ${blockers.map((b) => b.label).join(", ")}`);
      return;
    }
    // Phase 2A — precheck unmet items via the queue. If any completion_status != complete,
    // open the reason modal; otherwise close directly.
    const unmet = (queue?.items || []).filter((i) => i.completion_status && i.completion_status !== "complete");
    if (unmet.length === 0) {
      if (!window.confirm(t("Close today's session? Inventory already moved per finalized item."))) return;
      try { await api.post(`/prepare/session/${session.id}/close`, {}); await refresh(); toast.success(t("Session closed — kitchen ready")); }
      catch (e) { showCloseError(e); }
      return;
    }
    setCloseModal({ unmet, reasons: queue?.unmet_reasons || [] });
  };
  const submitCloseWithReasons = async (payload) => {
    try {
      await api.post(`/prepare/session/${session.id}/close`, { unmet_reasons: payload });
      setCloseModal(null); await refresh();
      toast.success(t("Session closed with unmet reasons recorded"));
    } catch (e) {
      showCloseError(e);
    }
  };
  const reopenSession = async () => { await openSession(); };
  const deleteItem = async (item) => {
    if (!window.confirm(t("Remove this ingredient from the session?"))) return;
    try { await api.delete(`/prepare/session/${session.id}/items/${item.item_id}`); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };
  const undoItem = async (item) => {
    if (!window.confirm(t("Undo this finalized ingredient? Stock will be reversed."))) return;
    try { await api.post(`/prepare/session/${session.id}/undo/${item.batch_id}`); await refresh(); toast.success(t("Reversed")); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  if (!data) return <PageLoader />;

  const session = data.session;
  const activeWh = data.warehouse || {};
  const isCentral = activeWh.scope === "central";
  const sum = session?.summary || {};

  const WarehouseControl = () => (canSwitch ? (
    <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2 shadow-sm" data-testid="mp-warehouse-switcher">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Warehouse size={16} /></span>
      <div className="leading-tight">
        <p className="text-[10px] font-semibold uppercase text-ink-mute">{t("Active Warehouse")}</p>
        <select value={whId || ""} onChange={(e) => setWhId(e.target.value)} data-testid="mp-warehouse-select" className="-ml-0.5 cursor-pointer bg-transparent text-sm font-bold text-ink focus:outline-none">
          {whList.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </div>
    </div>
  ) : (
    <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3.5 py-2 shadow-sm" data-testid="mp-active-warehouse">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Warehouse size={16} /></span>
      <div className="leading-tight">
        <p className="flex items-center gap-1 text-[10px] font-semibold uppercase text-ink-mute"><Lock size={9} /> {t("Active Warehouse")}</p>
        <p className="text-sm font-bold text-ink" data-testid="mp-active-warehouse-name">{activeWh.name || "—"}</p>
      </div>
    </div>
  ));

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="meja-prepare">
      <PageHeader icon={ChefHat} title={t("Meja Prepare")} subtitle={isCentral ? t("One daily session to prep the whole kitchen — Raw → Semi-Finished") : t("One daily session to prep the whole kitchen — Raw → Ready-to-Cook")}>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => setView(view === "history" ? "board" : "history")} data-testid="mp-history-toggle"><History size={15} /> {t("Session History")}</Button>
          <Button variant="outline" onClick={() => navigate("/meja-prepare/dashboard")} data-testid="mp-analytics-link"><BarChart3 size={15} /> {t("Analytics")}</Button>
          {canConfig && <Button variant="outline" onClick={() => navigate("/master-persiapan")} data-testid="mp-config-link"><Settings size={15} /> {t("Master Persiapan")}</Button>}
          <WarehouseControl />
        </div>
      </PageHeader>

      {view === "history" ? (
        <HistoryView hist={hist} onBack={() => setView("board")} t={t} />
      ) : !session ? (
        <NoSession dash={dash} canCreate={canCreate} busy={busy} onOpen={openSession} t={t} />
      ) : (
        <>
          {/* Session bar */}
          <Card className="mb-4 flex flex-wrap items-center gap-3 p-4" data-testid="mp-session-bar">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-white"><Soup size={22} /></span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="font-heading text-base font-extrabold text-ink">{t("Preparation Session")}</p>
                <Badge tone={session.status === "OPEN" ? "success" : "mute"}>{session.status === "OPEN" ? t("OPEN") : t("CLOSED")}</Badge>
              </div>
              <p className="text-xs text-ink-mute">{session.session_no} · {session.session_date} · {session.created_by_name}</p>
            </div>
            {canCreate && session.status === "OPEN" && (
              <>
                <Button onClick={() => setAddOpen(true)} data-testid="mp-add-item-btn"><Plus size={16} /> {t("Add Raw Material")}</Button>
                <Button variant="outline" onClick={closeSession} data-testid="mp-close-session-btn"><CheckCircle2 size={15} /> {t("Close Session")}</Button>
              </>
            )}
            {canCreate && session.status !== "OPEN" && <Button variant="outline" onClick={reopenSession} data-testid="mp-reopen-session-btn"><Play size={15} /> {t("Reopen Session")}</Button>}
          </Card>

          {/* Kitchen Readiness banner (Phase 2A) — reuses queue.readiness (SSOT). */}
          {queue?.readiness && queue.readiness.total_active > 0 && (
            <ReadinessBanner readiness={queue.readiness} t={t} />
          )}

          {/* KPIs */}
          <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5" data-testid="mp-kpis">
            <Kpi icon={Scale} tone="blue" value={`${fmtNum(sum.total_input || 0)}`} label={t("Total Input")} testid="mp-kpi-input" />
            <Kpi icon={PackageCheck} tone="emerald" value={`${fmtNum(sum.total_output || 0)}`} label={t("Total Output")} testid="mp-kpi-output" />
            <Kpi icon={Trash2} tone="orange" value={`${fmtNum(sum.total_waste || 0)}`} label={t("Total Waste")} testid="mp-kpi-waste" />
            <Kpi icon={Flame} tone="violet" value={sum.avg_yield != null ? `${sum.avg_yield}%` : "—"} label={t("Avg Yield")} testid="mp-kpi-yield" />
            <Kpi icon={CheckCircle2} tone="amber" value={`${sum.items_done || 0}/${sum.items_total || 0}`} label={t("Ingredients Done")} testid="mp-kpi-done" />
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
            {/* Daftar Bahan Hari Ini */}
            <div className="xl:col-span-7">
              {/* Phase 2B — Opening SOP (accordion, dari Kitchen SOP Engine) */}
              {sop && <SOPSection title="Opening SOP" icon={Sunrise} templates={sop.categories?.opening}
                whId={whId} canCreate={canCreate && session.status === "OPEN"} onChanged={loadSop}
                defaultOpen testid="sop-section-opening" />}
              {/* Antrian Persiapan Hari Ini — otomatis dari Master Persiapan */}
              {queue && queue.items && (
                <QueuePanel queue={queue} canCreate={canCreate && session.status === "OPEN"}
                  onPrepare={(item) => setWizardPreset({
                    source_product_id: item.source_product_id,
                    suggested_qty: item.need_prepare,
                    output_product_id: item.output_product_id,
                  })} />
              )}
              <Card className="p-4" data-testid="mp-items-panel">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="font-heading text-base font-extrabold text-ink">{t("Today's Ingredients")}</h3>
                  {canCreate && session.status === "OPEN" && <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-1 text-sm font-bold text-primary" data-testid="mp-add-item-link"><Plus size={15} /> {t("Add")}</button>}
                </div>
                {session.items.length === 0 ? (
                  <div className="py-10 text-center" data-testid="mp-items-empty">
                    <Sprout size={32} className="mx-auto mb-2 text-ink-mute" />
                    <p className="text-sm text-ink-mute">{t("No ingredients added yet")}</p>
                    {canCreate && session.status === "OPEN" && <Button className="mt-3" variant="outline" onClick={() => setAddOpen(true)} data-testid="mp-empty-add"><Plus size={15} /> {t("Add Raw Material")}</Button>}
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {session.items.map((it) => (
                      <ItemRow key={it.item_id} item={it} canCreate={canCreate && session.status === "OPEN"}
                        onFinalize={() => setFinItem(it)} onView={() => setViewItem(it)} onDelete={() => deleteItem(it)} onUndo={() => undoItem(it)} t={t} />
                    ))}
                  </div>
                )}
              </Card>

              {/* Phase 2B — During Operation / Food Safety / Closing SOP accordions */}
              {sop && <div className="mt-4">
                <SOPSection title="During Operation SOP" icon={Flame} templates={sop.categories?.during}
                  whId={whId} canCreate={canCreate && session.status === "OPEN"} onChanged={loadSop} testid="sop-section-during" />
                <SOPSection title="Food Safety" icon={ShieldCheck} templates={sop.categories?.food_safety}
                  whId={whId} canCreate={canCreate && session.status === "OPEN"} onChanged={loadSop} testid="sop-section-food-safety" />
                <SOPSection title="Closing SOP" icon={Moon} templates={sop.categories?.closing}
                  whId={whId} canCreate={canCreate && session.status === "OPEN"} onChanged={loadSop} testid="sop-section-closing" />
                {(sop.close_blockers || []).length > 0 && session.status === "OPEN" && (
                  <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-800" data-testid="sop-blockers-hint">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <div><b>{sop.close_blockers.length} SOP wajib</b> harus diselesaikan sebelum sesi dapat ditutup: {sop.close_blockers.map((b) => b.label).join(", ")}</div>
                  </div>
                )}
              </div>}
            </div>

            {/* Right: inventory + dashboard mini */}
            <div className="space-y-4 xl:col-span-5">
              <InventoryPanel rawInv={data.raw_inventory} rtcInv={data.rtc_inventory} t={t} />
              {dash && <DashboardMini dash={dash} onMore={() => navigate("/meja-prepare/dashboard")} t={t} />}
            </div>
          </div>
        </>
      )}

      {addOpen && session && <PrepWizard sid={session.id} raws={data.available_raws} reasons={data.reasons || REASONS} warehouse={whId} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); refresh(); }} />}
      {wizardPreset && session && <PrepWizard sid={session.id} raws={data.available_raws} reasons={data.reasons || REASONS} warehouse={whId} preset={wizardPreset} onClose={() => setWizardPreset(null)} onDone={() => { setWizardPreset(null); refresh(); }} />}
      {closeModal && <CloseSessionModal unmet={closeModal.unmet} reasons={closeModal.reasons} onClose={() => setCloseModal(null)} onSubmit={submitCloseWithReasons} t={t} />}
      {finItem && session && <FinalizeModal sid={session.id} item={finItem} reasons={data.reasons || REASONS} warehouse={whId} onClose={() => setFinItem(null)} onDone={() => { setFinItem(null); refresh(); }} />}
      {viewItem && <ViewItemModal item={viewItem} onClose={() => setViewItem(null)} t={t} />}
    </div>
  );
}

/* ---------------------------------- No session ---------------------------------- */
function NoSession({ dash, canCreate, busy, onOpen, t }) {
  return (
    <div className="space-y-5">
      <Card className="flex flex-col items-center gap-4 p-10 text-center" data-testid="mp-no-session">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-light text-primary"><Soup size={32} /></span>
        <div>
          <h3 className="font-heading text-xl font-extrabold text-ink">{t("No preparation session today")}</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-ink-mute">{t("After receiving stock from Central, open today's session to prepare the whole kitchen — one ingredient at a time.")}</p>
        </div>
        {canCreate && <Button size="lg" onClick={onOpen} loading={busy} data-testid="mp-open-session-btn"><Play size={18} /> {t("Open Preparation Session")}</Button>}
        {!canCreate && <p className="text-sm text-amber-700">{t("Log in as Head Chef to open a session.")}</p>}
      </Card>
      {dash && (dash.summary.items_done > 0) && <DashboardMini dash={dash} t={t} />}
    </div>
  );
}

/* ---------------------------------- Item row ---------------------------------- */
function ItemRow({ item, canCreate, onFinalize, onView, onDelete, onUndo, t }) {
  const done = item.status === "DONE";
  const produced = (item.outputs || []).filter((o) => Number(o.prepared_weight) > 0);
  return (
    <div className={`rounded-xl border p-3 ${done ? "border-emerald-200 bg-emerald-50/30" : "border-line bg-white"}`} data-testid={`mp-item-${item.item_id}`}>
      <div className="flex items-start gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${done ? "bg-emerald-100 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{done ? <PackageCheck size={20} /> : <Box size={20} />}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-heading text-sm font-extrabold text-ink">{item.source_name}</p>
            <Badge tone={done ? "success" : "warn"}>{done ? t("Done") : t("In Process")}</Badge>
            {item.review_required && <Badge tone="danger"><AlertTriangle size={11} /> {t("Review")}</Badge>}
          </div>
          <p className="text-xs text-ink-soft">{t("Input")}: <b>{fmtNum(item.input_qty)} {item.source_unit}</b>
            {done && <> · {t("Output")} {fmtNum(item.total_output)} · {t("Waste")} {fmtNum(item.waste)} · {t("Yield")} <b className={item.review_required ? "text-danger-text" : "text-emerald-600"}>{item.yield_pct}%</b></>}
          </p>
          {(done ? produced : item.outputs).length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {(done ? produced : item.outputs).slice(0, 6).map((o) => (
                <span key={o.output_product_id} className="rounded-full bg-app px-2 py-0.5 text-[10px] font-semibold text-ink-soft">
                  {o.output_name}{done ? ` ${fmtNum(o.prepared_weight)}${o.unit || ""}` : ""}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {!done && canCreate && <Button size="sm" onClick={onFinalize} data-testid={`mp-finalize-${item.item_id}`}><ListChecks size={14} /> {t("Fill & Finish")}</Button>}
          {done && <button onClick={onView} className="text-xs font-bold text-primary" data-testid={`mp-view-${item.item_id}`}>{t("Detail")}</button>}
          <div className="flex gap-1">
            {!done && canCreate && <button onClick={onDelete} className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-ink-mute hover:border-danger hover:text-danger-text" data-testid={`mp-del-${item.item_id}`}><Trash2 size={13} /></button>}
            {done && canCreate && <button onClick={onUndo} className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-ink-mute hover:border-amber-400 hover:text-amber-600" data-testid={`mp-undo-${item.item_id}`}><Undo2 size={13} /></button>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------- Inventory panel ---------------------------------- */
function InventoryPanel({ rawInv, rtcInv, t }) {
  const Row = ({ list, tone, empty, testid }) => (
    <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1" data-testid={testid}>
      {list.length === 0 ? <p className="py-3 text-center text-xs text-ink-mute">{empty}</p> :
        list.map((i) => (
          <div key={i.product_id || i.name} className="flex items-center justify-between gap-2 rounded-lg bg-app px-2.5 py-1.5">
            <span className="min-w-0 truncate text-xs font-semibold text-ink">{i.name}</span>
            <span className={`shrink-0 text-xs font-bold ${tone}`}>{fmtNum(i.current_stock)} {i.unit}</span>
          </div>
        ))}
    </div>
  );
  return (
    <Card className="p-4" data-testid="mp-inventory-panel">
      <h3 className="mb-2 font-heading text-sm font-extrabold text-ink">{t("Inventory Result")}</h3>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-600"><Boxes size={13} /> {t("Raw Inventory (Remaining)")}</div>
      <Row list={rawInv || []} tone="text-amber-700" empty={t("No raw materials")} testid="mp-raw-inv" />
      <div className="mb-1.5 mt-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-emerald-600"><Flame size={13} /> {t("Ready-to-Cook")}</div>
      <Row list={(rtcInv || []).filter((i) => i.current_stock > 0)} tone="text-emerald-700" empty={t("No ready-to-cook stock yet")} testid="mp-rtc-inv" />
    </Card>
  );
}

/* ---------------------------------- Dashboard mini ---------------------------------- */
function DashboardMini({ dash, onMore, t }) {
  const s = dash.summary;
  return (
    <Card className="p-4" data-testid="mp-dashboard-mini">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-sm font-extrabold text-ink">{t("Head Chef Dashboard")}</h3>
        {onMore && <button onClick={onMore} className="text-xs font-bold text-primary" data-testid="mp-dash-more">{t("Full report")} →</button>}
      </div>
      <div className="grid grid-cols-2 gap-2 text-center">
        <div className="rounded-lg bg-app p-2"><p className="font-heading text-lg font-extrabold text-ink">{fmtNum(s.total_input)}</p><p className="text-[10px] text-ink-mute">{t("Total Input")}</p></div>
        <div className="rounded-lg bg-app p-2"><p className="font-heading text-lg font-extrabold text-emerald-600">{fmtNum(s.total_output)}</p><p className="text-[10px] text-ink-mute">{t("Total Output")}</p></div>
        <div className="rounded-lg bg-app p-2"><p className="font-heading text-lg font-extrabold text-orange-600">{fmtNum(s.total_waste)}</p><p className="text-[10px] text-ink-mute">{t("Total Waste")}</p></div>
        <div className="rounded-lg bg-app p-2"><p className="font-heading text-lg font-extrabold text-violet-600">{s.avg_yield != null ? `${s.avg_yield}%` : "—"}</p><p className="text-[10px] text-ink-mute">{t("Avg Yield")}</p></div>
      </div>
      {dash.waste_breakdown?.length > 0 && (
        <div className="mt-3">
          <p className="mb-1.5 text-[11px] font-bold uppercase text-ink-mute">{t("Today's Waste")}</p>
          {dash.waste_breakdown.slice(0, 5).map((w) => (
            <div key={w.category} className="flex items-center justify-between text-xs"><span className="text-ink-soft">{w.category}</span><span className="font-bold text-ink">{fmtNum(w.qty)} ({w.pct}%)</span></div>
          ))}
        </div>
      )}
      {dash.top_outputs?.length > 0 && (
        <div className="mt-3">
          <p className="mb-1.5 text-[11px] font-bold uppercase text-ink-mute">{t("Top Output")}</p>
          {dash.top_outputs.slice(0, 5).map((o) => (
            <div key={o.output_name} className="flex items-center justify-between text-xs"><span className="truncate text-ink-soft">{o.output_name}</span><span className="font-bold text-ink">{fmtNum(o.qty)} {o.unit}</span></div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ------------------------------ Kitchen Readiness Banner (Phase 2A) ------------------------------ */
function ReadinessBanner({ readiness, t }) {
  const score = readiness.score ?? 0;
  const band = readiness.band || "not_ready";
  const meta = band === "ready"
    ? { label: t("Kitchen Ready"), tone: "bg-emerald-50 text-emerald-700 border-emerald-200", ring: "text-emerald-600", bar: "bg-emerald-500", emoji: "🟢" }
    : band === "partial"
      ? { label: t("Partial Readiness"), tone: "bg-amber-50 text-amber-800 border-amber-200", ring: "text-amber-600", bar: "bg-amber-500", emoji: "🟡" }
      : { label: t("Kitchen Not Ready"), tone: "bg-red-50 text-red-700 border-red-200", ring: "text-red-600", bar: "bg-red-500", emoji: "🔴" };
  return (
    <Card className={`mb-4 border ${meta.tone} p-4`} data-testid="mp-readiness-banner">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm">
            <span className={`font-heading text-2xl font-extrabold ${meta.ring}`} data-testid="mp-readiness-score">{score}%</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide">{t("Kitchen Readiness")}</p>
            <p className="font-heading text-lg font-extrabold" data-testid="mp-readiness-band">{meta.emoji} {meta.label}</p>
          </div>
        </div>
        <div className="min-w-[220px] flex-1">
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/80">
            <div className={`h-full rounded-full transition-all ${meta.bar}`} style={{ width: `${score}%` }} />
          </div>
          <p className="mt-1 text-[11px] opacity-80">
            {t("Base")}: <b>{readiness.base}%</b>
            {readiness.penalty > 0 && <> · {t("Kritis penalty")}: <b>−{readiness.penalty}%</b></>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge tone="success" data-testid="mp-readiness-complete">🟢 {readiness.complete || 0} {t("Complete")}</Badge>
          <Badge tone="warn" data-testid="mp-readiness-partial">🟡 {readiness.partial || 0} {t("Partial")}</Badge>
          <Badge tone="danger" data-testid="mp-readiness-notstarted">🔴 {readiness.not_started || 0} {t("Not Started")}</Badge>
          {readiness.critical > 0 && <Badge tone="danger" data-testid="mp-readiness-critical">⚠️ {readiness.critical} {t("Kritis")}</Badge>}
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------ Close Session Modal (Phase 2A) ------------------------------ */
function CloseSessionModal({ unmet, reasons, onClose, onSubmit, t }) {
  const [entries, setEntries] = useState(() => {
    const m = {};
    for (const u of unmet) m[u.output_product_id] = { reason: "", note: "" };
    return m;
  });
  const [busy, setBusy] = useState(false);
  const canSubmit = unmet.every((u) => {
    const e = entries[u.output_product_id];
    if (!e?.reason) return false;
    if (e.reason === "other" && !e.note.trim()) return false;
    return true;
  });
  const submit = async () => {
    setBusy(true);
    try {
      const payload = unmet.map((u) => ({
        output_product_id: u.output_product_id,
        reason: entries[u.output_product_id].reason,
        note: entries[u.output_product_id].note || null,
      }));
      await onSubmit(payload);
    } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={t("Tutup Sesi — Alasan Wajib untuk Target yang Belum Tercapai")} size="lg"
      footer={<div className="flex items-center justify-between gap-3">
        <span className="text-xs text-ink-mute">{unmet.length} {t("output belum mencapai target")}</span>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} data-testid="mp-close-modal-cancel">{t("Batal")}</Button>
          <Button onClick={submit} disabled={!canSubmit} loading={busy} data-testid="mp-close-modal-submit">
            <CheckCircle2 size={15} /> {t("Tutup Sesi")}
          </Button>
        </div>
      </div>}>
      <div className="space-y-3" data-testid="mp-close-modal">
        <div className="flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>{t("Setiap output yang belum mencapai Target Standby harus memiliki alasan. Alasan ini masuk ke audit trail untuk review Owner.")}</div>
        </div>
        {unmet.map((u) => {
          const e = entries[u.output_product_id] || { reason: "", note: "" };
          return (
            <div key={u.output_product_id} className="rounded-xl border border-line p-3" data-testid={`mp-close-item-${u.output_product_id}`}>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <p className="font-bold text-ink">{u.output_name}</p>
                <Badge tone={u.completion_status === "partial" ? "warn" : "danger"} className="!text-[9px]">
                  {u.completion_status === "partial" ? "🟡 Partial" : "🔴 Not Started"}
                </Badge>
                <span className="text-xs text-ink-mute">
                  {fmtNum(u.current_stock)}/{fmtNum(u.target_qty)} {u.unit}
                  {u.prepared_today > 0 && ` · Hari ini: ${fmtNum(u.prepared_today)} ${u.unit}`}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <select value={e.reason} onChange={(ev) => setEntries((s) => ({ ...s, [u.output_product_id]: { ...s[u.output_product_id], reason: ev.target.value } }))}
                  data-testid={`mp-close-reason-${u.output_product_id}`}
                  className="rounded-xl border border-line bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none">
                  <option value="">— {t("Pilih alasan")} —</option>
                  {(reasons || []).map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
                <input value={e.note} onChange={(ev) => setEntries((s) => ({ ...s, [u.output_product_id]: { ...s[u.output_product_id], note: ev.target.value } }))}
                  data-testid={`mp-close-note-${u.output_product_id}`}
                  placeholder={e.reason === "other" ? t("Catatan (wajib)") : t("Catatan (opsional)")}
                  className="col-span-2 rounded-xl border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none" />
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

/* ------------------------------ Antrian Persiapan Hari Ini (Phase 1) ------------------------------ */
/* Otomatis dihitung dari Master Persiapan (Target Standby − Stok Ready-to-Cook saat ini).
   Priority: kritis (di bawah min) → gap terbesar → alfabetis. */
function QueuePanel({ queue, canCreate, onPrepare }) {
  const items = queue.items || [];
  const s = queue.summary || {};
  const [expanded, setExpanded] = useState(true);
  if (!items.length && s.total === 0) {
    return (
      <Card className="mb-4 flex items-center gap-3 p-4" data-testid="mp-queue-empty">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><CheckCircle2 size={20} /></span>
        <div className="flex-1">
          <p className="font-heading text-sm font-extrabold text-ink">Antrian Persiapan Hari Ini</p>
          <p className="text-xs text-ink-mute">Belum ada target persiapan aktif. Atur di <b>Master Persiapan</b> agar antrian otomatis muncul.</p>
        </div>
      </Card>
    );
  }
  const statusMeta = (st) => (
    st === "critical" ? { label: "Kritis", tone: "danger", icon: AlertTriangle, ring: "border-red-200 bg-red-50/40" } :
    st === "need_prepare" ? { label: "Perlu Prepare", tone: "warn", icon: Target, ring: "border-orange-200 bg-orange-50/40" } :
    { label: "Sudah Siap", tone: "success", icon: CheckCircle2, ring: "border-emerald-200 bg-emerald-50/40" }
  );
  return (
    <Card className="mb-4 p-4" data-testid="mp-queue-panel">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-600"><Zap size={17} /></span>
          <div>
            <h3 className="font-heading text-base font-extrabold text-ink">Antrian Persiapan Hari Ini</h3>
            <p className="text-[11px] text-ink-mute">Otomatis: Target − Stok Siap Masak · {queue.warehouse?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {s.critical > 0 && <Badge tone="danger" data-testid="mp-queue-critical-count"><AlertTriangle size={11} /> {s.critical} Kritis</Badge>}
          {s.need_prepare > 0 && <Badge tone="warn" data-testid="mp-queue-need-count"><Target size={11} /> {s.need_prepare} Perlu</Badge>}
          {s.ready > 0 && <Badge tone="success" data-testid="mp-queue-ready-count"><CheckCircle2 size={11} /> {s.ready} Siap</Badge>}
          <button onClick={() => setExpanded((v) => !v)} className="text-xs font-bold text-primary" data-testid="mp-queue-toggle">{expanded ? "Sembunyikan" : "Tampilkan"}</button>
        </div>
      </div>
      {expanded && (
        <div className="space-y-2" data-testid="mp-queue-list">
          {items.map((it) => {
            const m = statusMeta(it.status);
            const Icon = m.icon;
            const noStock = it.source_current_stock <= 0;
            return (
              <div key={it.output_product_id} className={`flex items-start gap-3 rounded-xl border p-3 ${m.ring}`} data-testid={`mp-queue-item-${it.output_product_id}`}>
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                  it.status === "critical" ? "bg-red-100 text-red-600" :
                  it.status === "need_prepare" ? "bg-orange-100 text-orange-600" : "bg-emerald-100 text-emerald-600"
                }`}><Icon size={20} /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-heading text-sm font-extrabold text-ink">{it.output_name}</p>
                    <Badge tone={m.tone}>{m.label}</Badge>
                    {it.override_applied && <Badge tone="warn" className="!text-[9px]">Override Cabang</Badge>}
                  </div>
                  <p className="text-xs text-ink-soft">
                    dari <b>{it.source_name}</b> · Target <b>{fmtNum(it.target_qty)} {it.unit}</b>
                    {it.min_qty != null && <> · Min <b>{fmtNum(it.min_qty)} {it.unit}</b></>}
                    · Stok saat ini <b className={it.status === "ready" ? "text-emerald-600" : "text-orange-700"}>{fmtNum(it.current_stock)} {it.unit}</b>
                  </p>
                  {/* Phase 2A — progress bar (current_stock / target) + completion badge */}
                  <div className="mt-1.5 flex items-center gap-2" data-testid={`mp-queue-progress-${it.output_product_id}`}>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-stone-100">
                      <div className={`h-full rounded-full transition-all ${
                        it.completion_status === "complete" ? "bg-emerald-500" :
                        it.status === "critical" ? "bg-red-500" :
                        it.completion_status === "partial" ? "bg-amber-500" : "bg-orange-400"
                      }`} style={{ width: `${it.completion_pct ?? 0}%` }} />
                    </div>
                    <span className="text-[11px] font-bold text-ink-soft w-14 text-right" data-testid={`mp-queue-pct-${it.output_product_id}`}>{it.completion_pct ?? 0}%</span>
                    <Badge tone={
                      it.completion_status === "complete" ? "success" :
                      it.completion_status === "partial" ? "warn" : "danger"
                    } data-testid={`mp-queue-completion-${it.output_product_id}`} className="!text-[9px]">
                      {it.completion_status === "complete" ? "🟢 Complete" :
                       it.completion_status === "partial" ? "🟡 Partial" : "🔴 Not Started"}
                    </Badge>
                  </div>
                  {it.prepared_today > 0 && (
                    <p className="mt-0.5 text-[11px] text-emerald-700">
                      Hari ini: <b>{fmtNum(it.prepared_today)} {it.unit}</b> sudah di-prepare
                    </p>
                  )}
                  {it.status !== "ready" && (
                    <p className="mt-0.5 text-xs font-bold text-ink">
                      Perlu prepare: <span className={it.status === "critical" ? "text-red-600" : "text-orange-600"}>{fmtNum(it.need_prepare)} {it.unit}</span>
                    </p>
                  )}
                </div>
                {canCreate && it.status !== "ready" && (
                  <Button size="sm" disabled={noStock} onClick={() => onPrepare(it)} data-testid={`mp-queue-prep-${it.output_product_id}`}>
                    <Play size={13} /> Prepare
                  </Button>
                )}
                {noStock && it.status !== "ready" && (
                  <div className="ml-2 text-[10px] text-danger-text" title="Bahan baku habis"><AlertTriangle size={12} /> No stock</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}



/* ------------------------------ 3-Step Prep Wizard ------------------------------ */
/* Step 1 pick raw + input qty → Step 2 select outputs → Step 3 enter actual results.
   Reuses the existing engine: POST /items (add draft) then /finalize (or PUT for draft). */
function PrepWizard({ sid, raws, reasons, warehouse, preset, onClose, onDone }) {
  const { t } = useT();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [qty, setQty] = useState("");
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [weights, setWeights] = useState({});
  const [selReasons, setSelReasons] = useState([]);
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  // Preset from Antrian Persiapan: pre-select raw + suggest qty + pre-tick target output.
  useEffect(() => {
    if (!preset || !raws || raws.length === 0) return;
    const raw = raws.find((r) => r.source_product_id === preset.source_product_id);
    if (!raw) return;
    setSel(raw);
    if (preset.suggested_qty && !qty) setQty(String(preset.suggested_qty));
    if (preset.output_product_id) {
      setWeights((w) => ({ ...w, [preset.output_product_id]: w[preset.output_product_id] ?? "" }));
    }
    setStep(2);
  }, [preset, raws]);

  const uploadPrepPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/prepare/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  useEffect(() => {
    if (sel) api.get(`/prepare/source/${sel.source_product_id}/batches`, { params: warehouse ? { warehouse } : {} }).then(({ data }) => setBatches(data.batches || [])).catch(() => setBatches([]));
  }, [sel, warehouse]);

  const q = search.trim().toLowerCase();
  const list = (raws || []).filter((r) => !q || r.source_name.toLowerCase().includes(q));
  const input = Number(qty) || 0;
  const factor = sel?.input_factor || 1;

  const calc = useMemo(() => {
    const effIn = input * factor;
    const lines = (sel?.outputs || []).filter((o) => weights[o.output_product_id] != null).map((o) => {
      const w = Number(weights[o.output_product_id]) || 0;
      const base = w * (o.conversion_factor || 1);
      const y = effIn > 0 ? r1((base / effIn) * 100) : 0;
      const tol = o.yield_tolerance_pct ?? 8;
      return { ...o, w, base, yieldPct: y, review: w > 0 && o.expected_yield_pct != null && effIn > 0 && Math.abs(y - o.expected_yield_pct) > tol };
    });
    const totalBase = r1(lines.reduce((s, l) => s + l.base, 0));
    const overall = effIn > 0 ? r1((totalBase / effIn) * 100) : 0;
    return { lines, effIn: r1(effIn), totalBase, overall, waste: r1(effIn - totalBase), wastePct: r1(100 - overall), valid: input > 0 && totalBase > 0 && totalBase <= effIn + 1e-6 };
  }, [sel, weights, input, factor]);

  const toggleOut = (id, on) => setWeights((w) => { const n = { ...w }; if (on) delete n[id]; else n[id] = n[id] || ""; return n; });
  const pickedCount = Object.keys(weights).length;

  const addDraft = async () => {
    const { data: s } = await api.post(`/prepare/session/${sid}/items`, { source_product_id: sel.source_product_id, input_qty: input, batch_id: batchId || null });
    const drafts = (s.items || []).filter((i) => i.status === "DRAFT" && i.source_product_id === sel.source_product_id);
    drafts.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
    if (!drafts[0]) throw new Error("draft");
    return drafts[0].item_id;
  };

  const save = async (finish) => {
    if (finish && !calc.valid) return;
    if (finish && photos.length === 0) { toast.error(t("Photo evidence is required (min 1)")); return; }
    setBusy(true);
    try {
      const itemId = await addDraft();
      if (finish) {
        const outputs = Object.entries(weights).filter(([, v]) => Number(v) > 0).map(([id, v]) => ({ output_product_id: id, prepared_weight: Number(v) }));
        await api.post(`/prepare/session/${sid}/items/${itemId}/finalize`, { input_qty: input, outputs, reasons: selReasons, notes, photos });
        toast.success(`${sel.source_name} — ${t("finalized & moved to Ready-to-Cook")}`);
      } else {
        await api.put(`/prepare/session/${sid}/items/${itemId}`, { input_qty: input, outputs: Object.entries(weights).map(([id, v]) => ({ output_product_id: id, prepared_weight: v === "" ? null : Number(v) })), reasons: selReasons, notes });
        toast.success(t("Draft saved"));
      }
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? (d.message || `${d.current_stock || ""} ${d.unit || ""}`) : (d || t("Failed")));
    } finally { setBusy(false); }
  };

  const STEPS = [
    { n: 1, label: "Pilih Bahan Baku" },
    { n: 2, label: "Pilih Hasil Persiapan" },
    { n: 3, label: "Input Hasil Aktual" },
  ];

  const footer = (
    <div className="flex items-center justify-between gap-3">
      {step > 1 ? <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="wiz-back"><ArrowLeft size={15} /> Kembali</Button> : <span />}
      {step === 1 && <Button onClick={() => setStep(2)} disabled={!sel || !(input > 0)} data-testid="wiz-next-1">Lanjut <ArrowRight size={15} /></Button>}
      {step === 2 && <Button onClick={() => setStep(3)} disabled={pickedCount === 0} data-testid="wiz-next-2">Lanjut <ArrowRight size={15} /></Button>}
      {step === 3 && <div className="flex gap-2">
        <Button variant="outline" onClick={() => save(false)} loading={busy} data-testid="wiz-save-draft"><Save size={15} /> Simpan Draft</Button>
        <Button onClick={() => save(true)} disabled={!calc.valid || photos.length === 0} loading={busy} data-testid="wiz-save-finish"><PackageCheck size={16} /> Simpan Hasil</Button>
      </div>}
    </div>
  );

  return (
    <Modal open onClose={onClose} title="Persiapan Baru — 3 Langkah" size="lg" footer={footer}>
      <div className="space-y-4" data-testid="prep-wizard">
        {/* Stepper */}
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.n}>
              <div className="flex items-center gap-2">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-extrabold ${step >= s.n ? "bg-primary text-white" : "bg-app text-ink-mute"}`}>{s.n}</span>
                <span className={`hidden text-xs font-bold sm:block ${step >= s.n ? "text-ink" : "text-ink-mute"}`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <span className={`mx-1 h-px flex-1 ${step > s.n ? "bg-primary" : "bg-line"}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1 — raw + qty */}
        {step === 1 && (
          <div className="space-y-3" data-testid="wiz-step-1">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="wiz-search" placeholder="Cari bahan baku…" className="w-full rounded-xl border border-line bg-white py-2.5 pl-9 pr-3 text-sm focus:border-primary focus:outline-none" />
            </div>
            <div className="grid max-h-56 grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
              {list.length === 0 && <p className="col-span-full py-4 text-center text-xs text-ink-mute">{t("No configured raw materials")}</p>}
              {list.map((r) => {
                const on = sel?.source_product_id === r.source_product_id;
                return (
                  <button key={r.source_product_id} type="button" disabled={!r.source_available} onClick={() => { setSel(r); setWeights({}); }} data-testid={`wiz-raw-${r.source_product_id}`}
                    className={`flex items-center gap-2 rounded-xl border p-2.5 text-left transition disabled:opacity-50 ${on ? "border-primary bg-primary-light" : "border-line bg-white hover:bg-stone-50"}`}>
                    <ProductAvatar image={r.image} colorKey={r.category_name} name={r.source_name} size="sm" />
                    <div className="min-w-0"><p className="truncate text-sm font-bold text-ink">{r.source_name}</p><p className="text-[10px] text-ink-mute">{fmtNum(r.current_stock)} {r.unit} · {r.output_count} {t("outputs")}</p></div>
                  </button>
                );
              })}
            </div>
            {sel && (
              <div className="rounded-xl border border-line bg-app p-3" data-testid="wiz-detail">
                <p className="mb-2 text-sm font-bold text-ink">{sel.source_name} <span className="text-xs font-normal text-ink-mute">· {t("Stock")} {fmtNum(sel.current_stock)} {sel.unit}</span></p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs font-semibold text-ink-soft">{t("Input Quantity")} ({sel.unit})</span>
                    <input type="number" min="0" step="0.01" value={qty} autoFocus onChange={(e) => setQty(e.target.value)} data-testid="wiz-qty" className="mt-1 w-full rounded-xl border border-line py-2 px-3 text-lg font-bold focus:border-primary focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-ink-soft">{t("Select Batch")}</span>
                    <select value={batchId} onChange={(e) => setBatchId(e.target.value)} data-testid="wiz-batch" className="mt-1 w-full rounded-xl border border-line bg-white py-2 px-3 text-sm focus:border-primary focus:outline-none">
                      <option value="">{t("Latest batch (auto)")}</option>
                      {batches.map((b) => <option key={b.id} value={b.id}>{b.batch_number || t("No batch")} · {fmtNum(b.quantity)}</option>)}
                    </select>
                  </label>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — select outputs */}
        {step === 2 && sel && (
          <div className="space-y-2" data-testid="wiz-step-2">
            <p className="text-xs font-semibold text-ink-soft">Pilih hasil persiapan dari <b className="text-ink">{sel.source_name}</b> ({input} {sel.unit}):</p>
            {(sel.outputs || []).length === 0 && <p className="py-6 text-center text-sm text-ink-mute">Belum ada output untuk bahan ini. Atur dulu di Master Persiapan.</p>}
            {(sel.outputs || []).map((o) => {
              const on = weights[o.output_product_id] != null;
              return (
                <button key={o.output_product_id} type="button" onClick={() => toggleOut(o.output_product_id, on)} data-testid={`wiz-out-${o.output_product_id}`}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ${on ? "border-primary bg-primary-light/40" : "border-line bg-white hover:bg-stone-50"}`}>
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on ? "border-primary bg-primary text-white" : "border-line"}`}>{on && <CheckCircle2 size={13} />}</span>
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-ink">{o.output_name}</p><p className="text-[10px] text-ink-mute">{t("Standard Yield")}: {o.expected_yield_pct != null ? `${o.expected_yield_pct}%` : "—"} · {o.unit}</p></div>
                </button>
              );
            })}
          </div>
        )}

        {/* STEP 3 — actual results */}
        {step === 3 && sel && (
          <div className="space-y-4" data-testid="wiz-step-3">
            <div>
              <p className="mb-1.5 text-xs font-semibold text-ink-soft">Input berat aktual tiap hasil persiapan</p>
              <div className="space-y-2">
                {calc.lines.map((l) => (
                  <div key={l.output_product_id} className={`flex items-center gap-2 rounded-xl border p-2.5 ${l.review ? "border-danger bg-danger-light/40" : "border-line"}`} data-testid={`wiz-line-${l.output_product_id}`}>
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-ink">{l.output_name}</p><p className="text-[10px] text-ink-mute">{t("Standard Yield")}: {l.expected_yield_pct != null ? `${l.expected_yield_pct}%` : "—"}{l.w > 0 ? ` · ${t("Actual")} ${l.yieldPct}%` : ""}</p></div>
                    <input type="number" min="0" step="0.01" value={weights[l.output_product_id] || ""} onChange={(e) => setWeights((w) => ({ ...w, [l.output_product_id]: e.target.value }))} data-testid={`wiz-weight-${l.output_product_id}`} placeholder="0" className="w-24 rounded-lg border border-line py-1.5 px-2 text-right text-base font-bold focus:border-primary focus:outline-none" />
                    <span className="w-8 text-xs text-ink-mute">{l.unit}</span>
                  </div>
                ))}
              </div>
            </div>
            {calc.totalBase > 0 && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="wiz-calc">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center"><p className="font-heading text-2xl font-extrabold text-emerald-700">{fmtNum(calc.totalBase)}</p><p className="text-[10px] font-semibold uppercase text-ink-mute">{t("Total Output")}</p></div>
                <div className="rounded-xl border border-line p-3 text-center"><p className="font-heading text-2xl font-extrabold text-ink">{calc.overall}%</p><p className="text-[10px] font-semibold uppercase text-ink-mute">{t("Yield")}</p></div>
                <div className="rounded-xl border border-line p-3 text-center"><p className="font-heading text-2xl font-extrabold text-orange-600">{fmtNum(calc.waste)}</p><p className="text-[10px] font-semibold uppercase text-ink-mute">{t("Waste")}</p></div>
                <div className="rounded-xl border border-line p-3 text-center"><p className="font-heading text-2xl font-extrabold text-orange-600">{calc.wastePct}%</p><p className="text-[10px] font-semibold uppercase text-ink-mute">{t("Waste")} %</p></div>
              </div>
            )}
            {calc.totalBase > calc.effIn && <p className="text-xs font-semibold text-danger-text"><AlertTriangle size={12} className="mr-1 inline" />{t("Total result cannot exceed input")}</p>}
            <div>
              <p className="mb-1.5 text-xs font-semibold text-ink-soft">{t("Waste Reason")} ({t("optional")})</p>
              <div className="flex flex-wrap gap-2">
                {reasons.map((rsn) => {
                  const on = selReasons.includes(rsn);
                  return <button key={rsn} type="button" onClick={() => setSelReasons((a) => on ? a.filter((x) => x !== rsn) : [...a, rsn])} data-testid={`wiz-reason-${rsn}`}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${on ? "border-primary bg-primary text-white" : "border-line bg-white text-ink-soft hover:bg-stone-50"}`}>{t(`reason_${rsn}`)}</button>;
                })}
              </div>
            </div>
            <label className="block">
              <span className="text-xs font-semibold text-ink-soft">{t("Notes")} ({t("optional")})</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="wiz-notes" className="mt-1 w-full rounded-xl border border-line py-2 px-3 text-sm focus:border-primary focus:outline-none" />
            </label>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <PhotoEvidence
                tokens={photos} setTokens={setPhotos} uploadFile={uploadPrepPhoto}
                Thumb={({ token }) => <PrepPhoto path={token} alt="bukti" className="h-full w-full object-cover" />}
                max={6} testid="wiz-photos"
                label={`${t("Photo Evidence")} (${t("required")})`}
                hint={t("Attach at least 1 photo of the preparation result")}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ---------------------------------- Finalize modal ---------------------------------- */
function FinalizeModal({ sid, item, reasons, warehouse, onClose, onDone }) {
  const { t } = useT();
  const toast = useToast();
  const [qty, setQty] = useState(String(item.input_qty ?? ""));
  const [weights, setWeights] = useState(() => {
    const m = {}; (item.outputs || []).forEach((o) => { if (o.prepared_weight != null) m[o.output_product_id] = String(o.prepared_weight); }); return m;
  });
  const [selReasons, setSelReasons] = useState(item.reasons || []);
  const [notes, setNotes] = useState(item.notes || "");
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [finishing, setFinishing] = useState(false);

  const input = Number(qty) || 0;
  const factor = item.input_factor || 1;
  const calc = useMemo(() => {
    const effIn = input * factor;
    const lines = (item.outputs || []).map((o) => {
      const w = Number(weights[o.output_product_id]) || 0;
      const base = w * (o.conversion_factor || 1);
      const y = effIn > 0 ? r1((base / effIn) * 100) : 0;
      const exp = o.expected_yield_pct, tol = o.yield_tolerance_pct ?? 8;
      return { ...o, w, base, yieldPct: y, review: w > 0 && exp != null && effIn > 0 && Math.abs(y - exp) > tol };
    });
    const totalBase = r1(lines.reduce((s, l) => s + l.base, 0));
    const overall = effIn > 0 ? r1((totalBase / effIn) * 100) : 0;
    return { lines, effIn: r1(effIn), totalBase, overall, waste: r1(effIn - totalBase), wastePct: r1(100 - overall), valid: input > 0 && totalBase > 0 && totalBase <= effIn + 1e-6 };
  }, [item.outputs, weights, input, factor]);

  const toggle = (id, on) => setWeights((w) => { const n = { ...w }; if (on) delete n[id]; else n[id] = n[id] || ""; return n; });

  const uploadPhoto = async (file, which) => {
    if (!file) return;
    setUploading(which);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/prepare/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const val = { path: data.path, preview: URL.createObjectURL(file) };
      which === "before" ? setBefore(val) : setAfter(val);
    } catch { toast.error(t("Failed to upload photo")); } finally { setUploading(null); }
  };

  const payloadOutputs = () => Object.entries(weights).filter(([, v]) => Number(v) > 0).map(([id, v]) => ({ output_product_id: id, prepared_weight: Number(v) }));

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      await api.put(`/prepare/session/${sid}/items/${item.item_id}`, { input_qty: input, outputs: Object.entries(weights).map(([id, v]) => ({ output_product_id: id, prepared_weight: v === "" ? null : Number(v) })), reasons: selReasons, notes });
      toast.success(t("Draft saved")); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); } finally { setSavingDraft(false); }
  };

  const finalize = async () => {
    if (!calc.valid) return;
    setFinishing(true);
    try {
      await api.post(`/prepare/session/${sid}/items/${item.item_id}/finalize`, { input_qty: input, outputs: payloadOutputs(), reasons: selReasons, notes, before_photo: before?.path, after_photo: after?.path });
      toast.success(`${item.source_name} — ${t("finalized & moved to Ready-to-Cook")}`); onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? (d.message || t("Failed")) : (d || t("Failed")));
    } finally { setFinishing(false); }
  };

  return (
    <Modal open onClose={onClose} title={`${t("Prepare")}: ${item.source_name}`} size="lg"
      footer={<div className="flex items-center justify-between gap-3">
        <Button variant="outline" onClick={saveDraft} loading={savingDraft} data-testid="mp-savedraft-btn"><Save size={15} /> {t("Save Draft")}</Button>
        <Button onClick={finalize} disabled={!calc.valid || !(before?.path || after?.path)} loading={finishing} data-testid="mp-finalize-confirm"><PackageCheck size={16} /> {t("Save & Finish")}</Button>
      </div>}>
      <div className="space-y-4" data-testid="mp-finalize-modal">
        <label className="block">
          <span className="text-xs font-semibold text-ink-soft">{t("Input Quantity")} ({item.source_unit})</span>
          <input type="number" min="0" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="mp-fin-input"
            className="mt-1 w-40 rounded-xl border border-line py-2 px-3 text-lg font-bold focus:border-primary focus:outline-none" />
          {factor !== 1 && <span className="ml-2 text-xs text-ink-mute">= {fmtNum(calc.effIn)} {item.base_unit}</span>}
        </label>

        <div>
          <p className="mb-1.5 text-xs font-semibold text-ink-soft">{t("Select outputs produced today & enter actual weight")}</p>
          <div className="space-y-2">
            {calc.lines.map((l) => {
              const on = weights[l.output_product_id] != null;
              return (
                <div key={l.output_product_id} className={`rounded-xl border p-2.5 ${l.review ? "border-danger bg-danger-light/40" : on ? "border-primary/40 bg-primary-light/30" : "border-line"}`} data-testid={`mp-fin-out-${l.output_product_id}`}>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => toggle(l.output_product_id, on)} className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on ? "border-primary bg-primary text-white" : "border-line"}`} data-testid={`mp-fin-toggle-${l.output_product_id}`}>{on && <CheckCircle2 size={13} />}</button>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-ink">{l.output_name}</p>
                      <p className="text-[10px] text-ink-mute">{t("Standard Yield")}: {l.expected_yield_pct != null ? `${l.expected_yield_pct}%` : "—"}{l.w > 0 ? ` · ${t("Actual")} ${l.yieldPct}%` : ""}</p>
                    </div>
                    {on && <div className="flex items-center gap-1.5">
                      <input type="number" min="0" step="0.01" value={weights[l.output_product_id] || ""} onChange={(e) => setWeights((w) => ({ ...w, [l.output_product_id]: e.target.value }))} data-testid={`mp-fin-weight-${l.output_product_id}`}
                        placeholder="0" className="w-24 rounded-lg border border-line py-1.5 px-2 text-right text-base font-bold focus:border-primary focus:outline-none" />
                      <span className="w-8 text-xs text-ink-mute">{l.unit}</span>
                    </div>}
                  </div>
                  {l.review && <p className="mt-1 text-[11px] font-semibold text-danger-text"><AlertTriangle size={11} className="mr-1 inline" />{t("Yield outside expected range")}</p>}
                </div>
              );
            })}
          </div>
        </div>

        {calc.totalBase > 0 && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="mp-fin-calc">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center"><p className="font-heading text-2xl font-extrabold text-emerald-700">{fmtNum(calc.totalBase)}</p><p className="text-[10px] font-semibold uppercase text-ink-mute">{t("Total Output")}</p></div>
            <div className="rounded-xl border border-line p-3 text-center"><p className="font-heading text-2xl font-extrabold text-ink">{calc.overall}%</p><p className="text-[10px] font-semibold uppercase text-ink-mute">{t("Yield")}</p></div>
            <div className="rounded-xl border border-line p-3 text-center"><p className="font-heading text-2xl font-extrabold text-orange-600">{fmtNum(calc.waste)}</p><p className="text-[10px] font-semibold uppercase text-ink-mute">{t("Waste")}</p></div>
            <div className="rounded-xl border border-line p-3 text-center"><p className="font-heading text-2xl font-extrabold text-orange-600">{calc.wastePct}%</p><p className="text-[10px] font-semibold uppercase text-ink-mute">{t("Waste")} %</p></div>
          </div>
        )}
        {calc.totalBase > calc.effIn && <p className="text-xs font-semibold text-danger-text"><AlertTriangle size={12} className="mr-1 inline" />{t("Total result cannot exceed input")}</p>}

        <div>
          <p className="mb-1.5 text-xs font-semibold text-ink-soft">{t("Waste Reason")} ({t("optional")})</p>
          <div className="flex flex-wrap gap-2">
            {reasons.map((rsn) => {
              const on = selReasons.includes(rsn);
              return <button key={rsn} type="button" onClick={() => setSelReasons((a) => on ? a.filter((x) => x !== rsn) : [...a, rsn])} data-testid={`mp-reason-${rsn}`}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${on ? "border-primary bg-primary text-white" : "border-line bg-white text-ink-soft hover:bg-stone-50"}`}>{t(`reason_${rsn}`)}</button>;
            })}
          </div>
        </div>

        <label className="block">
          <span className="text-xs font-semibold text-ink-soft">{t("Notes")}</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mp-fin-notes" className="mt-1 w-full rounded-xl border border-line py-2 px-3 text-sm focus:border-primary focus:outline-none" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <PhotoUpload which="before" value={before} uploading={uploading === "before"} onPick={uploadPhoto} t={t} />
          <PhotoUpload which="after" value={after} uploading={uploading === "after"} onPick={uploadPhoto} t={t} />
        </div>
      </div>
    </Modal>
  );
}

/* ---------------------------------- View done item ---------------------------------- */
function ViewItemModal({ item, onClose, t }) {
  return (
    <Modal open onClose={onClose} title={`${item.source_name} · ${item.prep_no || ""}`} size="md">
      <div className="space-y-4" data-testid="mp-view-modal">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[[t("Input"), `${fmtNum(item.input_qty)} ${item.source_unit}`], [t("Total Output"), fmtNum(item.total_output)], [t("Yield"), `${item.yield_pct}%`], [t("Waste"), fmtNum(item.waste)]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-line p-3"><p className="text-[10px] font-semibold uppercase text-ink-mute">{l}</p><p className="font-bold text-ink">{v}</p></div>
          ))}
        </div>
        <div className="rounded-xl border border-line overflow-hidden">
          <div className="border-b border-line bg-app px-3 py-2 text-[11px] font-bold uppercase text-ink-mute">{t("Outputs Produced")}</div>
          <div className="divide-y divide-line">
            {(item.outputs || []).filter((o) => Number(o.prepared_weight) > 0).map((o) => (
              <div key={o.output_product_id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="font-semibold text-ink">{o.output_name}</span>
                <span className="text-ink-soft">{fmtNum(o.prepared_weight)} {o.unit} · <b className={o.review_required ? "text-danger-text" : "text-emerald-600"}>{o.yield_pct}%</b>{o.expected_yield_pct != null ? ` / ${o.expected_yield_pct}%` : ""}</span>
              </div>
            ))}
          </div>
        </div>
        {item.source_batch?.supplier_name && <p className="text-sm text-ink-soft">{t("Supplier")}: {item.source_batch.supplier_name}{item.source_batch.batch_number ? ` · ${t("Batch")}: ${item.source_batch.batch_number}` : ""}</p>}
        {item.reasons?.length > 0 && <p className="text-sm text-ink-soft">{t("Waste Reason")}: {item.reasons.map((r) => t(`reason_${r}`)).join(", ")}</p>}
        {item.notes && <p className="text-sm text-ink-soft">{t("Notes")}: {item.notes}</p>}
        {item.completed_by_name && <p className="text-xs text-emerald-600"><CheckCircle2 size={12} className="mr-1 inline" />{t("Completed")} · {item.completed_by_name}</p>}
      </div>
    </Modal>
  );
}

/* ---------------------------------- History ---------------------------------- */
function HistoryView({ hist, onBack, t }) {
  const navigate = useNavigate();
  return (
    <>
      <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm font-bold text-primary" data-testid="mp-history-back"><ChevronLeft size={16} /> {t("Back to Meja Prepare")}</button>
      <Card data-testid="mp-history">
        {!hist ? <PageLoader /> : hist.length === 0 ? <p className="py-10 text-center text-ink-mute">{t("No sessions yet")}</p> : (
          <div className="divide-y divide-line">
            {hist.map((s) => (
              <button key={s.id} onClick={() => navigate(`/meja-prepare/dashboard?date=${s.session_date}`)} data-testid={`mp-hist-${s.session_no}`} className="flex w-full items-center gap-3 py-3 text-left hover:bg-app">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-light text-primary"><Soup size={18} /></span>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-ink">{s.session_no} <Badge tone={s.status === "OPEN" ? "success" : "mute"}>{s.status === "OPEN" ? t("OPEN") : t("CLOSED")}</Badge></p>
                  <p className="text-xs text-ink-mute">{s.session_date} · {s.created_by_name} · {s.items_done} {t("ingredients")}</p>
                </div>
                <div className="text-right text-xs text-ink-soft">
                  <p>{t("In")} {fmtNum(s.total_input)} · {t("Out")} {fmtNum(s.total_output)}</p>
                  {s.avg_yield != null && <p className="font-bold text-emerald-600">{t("Yield")} {s.avg_yield}%</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
